import { useCallback, useEffect, useState } from 'react';
import { AdminFormPageShimmer } from '../../common/Shimmer.jsx';
import { useAdminDialog } from '../../../contexts/AdminDialogContext.jsx';
import { hpApi, formatDateTime } from './hpApi.js';
import { HpIcon, HpField, HpBadge, useHpToast, useUnsavedGuard } from './HpUi.jsx';

const PORTAL_DOCS = [
  { type: 'privacy', label: 'プライバシーポリシー', path: '/privacypolicy/' },
  { type: 'terms', label: '利用規約', path: '/terms/' },
  { type: 'commerce', label: '特定商取引法に基づく表記', path: '/commerce/' },
];

function PortalDocs({ siteUrl, onOpenLegal }) {
  const [docs, setDocs] = useState({});

  useEffect(() => {
    let alive = true;
    PORTAL_DOCS.forEach(async ({ type }) => {
      try {
        const res = await hpApi.getPortalLegal(type);
        if (alive) setDocs((d) => ({ ...d, [type]: res }));
      } catch (err) {
        if (alive) setDocs((d) => ({ ...d, [type]: { error: err.message || '取得できませんでした' } }));
      }
    });
    return () => { alive = false; };
  }, []);

  return (
    <section className="hp-card">
      <div className="hp-card__head">
        <h3 className="hp-card__title">Synapse と共通の規約</h3>
        {onOpenLegal && (
          <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={onOpenLegal}>「規約改定」で編集する<HpIcon.External size={12} /></button>
        )}
      </div>
      <p className="hp-note"><HpIcon.Lock />この3つの文書は「規約改定」で公開した最新版がそのまま HP に表示されます（10分以内に反映）。ここでは編集できません。</p>
      <ul className="hp-legal-list">
        {PORTAL_DOCS.map(({ type, label, path }) => {
          const d = docs[type];
          return (
            <li key={type} className="hp-legal-list__item">
              <div>
                <span className="hp-legal-list__title">{label}</span>
                <span className="hp-legal-list__meta">
                  {!d ? '読み込み中…' : d.error ? <span className="hp-text-danger">{d.error}</span> : `${d.lastPublishedVersion || '—'}・公開 ${formatDateTime(d.lastPublishedAt)}・${d.articleCount} 条`}
                </span>
              </div>
              <a className="hp-link" href={`${siteUrl.replace(/\/$/, '')}${path}`} target="_blank" rel="noreferrer"><HpIcon.External />HPで見る</a>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function CrossborderEditor({ canWrite, siteUrl }) {
  const toast = useHpToast();
  const { showConfirm } = useAdminDialog();
  const [doc, setDoc] = useState(null);
  const [open, setOpen] = useState(() => new Set());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  useUnsavedGuard(dirty);

  const load = useCallback(async () => {
    try {
      const res = await hpApi.getCrossborder();
      setDoc(res);
      setDirty(false);
    } catch (err) {
      toast.error(err.message || '文書の取得に失敗しました');
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  if (!doc) return <AdminFormPageShimmer fields={4} withHeader={false} />;

  const set = (patch) => { setDoc((d) => ({ ...d, ...patch })); setDirty(true); };
  const setArticle = (i, patch) => set({ articles: doc.articles.map((a, j) => (j === i ? { ...a, ...patch } : a)) });
  const setSub = (i, k, patch) => setArticle(i, { subArticles: doc.articles[i].subArticles.map((s, j) => (j === k ? { ...s, ...patch } : s)) });
  const toggle = (i) => setOpen((s) => { const n = new Set(s); if (n.has(i)) n.delete(i); else n.add(i); return n; });
  const moveArticle = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= doc.articles.length) return;
    const list = [...doc.articles];
    [list[i], list[j]] = [list[j], list[i]];
    set({ articles: list });
  };
  const removeArticle = async (i) => {
    const ok = await showConfirm({ title: '条項を削除しますか', message: `「${doc.articles[i].heading}」を削除します。保存するまで HP には反映されません。`, confirmLabel: '削除', danger: true });
    if (ok) set({ articles: doc.articles.filter((_, j) => j !== i) });
  };

  const save = async () => {
    if (!doc.title.trim() || !doc.updatedLabel.trim()) {
      toast.error('タイトルと最終更新日を入力してください');
      return;
    }
    if (doc.articles.some((a) => !a.heading.trim() || a.subArticles.some((s) => !s.heading.trim()))) {
      toast.error('見出しが空の条項があります');
      return;
    }
    setSaving(true);
    try {
      await hpApi.saveCrossborder({ title: doc.title, updatedLabel: doc.updatedLabel, articles: doc.articles });
      toast.success('保存しました。HP にはおよそ1分以内に反映されます');
      await load();
    } catch (err) {
      toast.error(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="hp-card">
      <div className="hp-card__head">
        <h3 className="hp-card__title">外国にある第三者への提供に関する公表事項（HP 専用）</h3>
        <a className="hp-link" href={`${siteUrl.replace(/\/$/, '')}/crossborder/`} target="_blank" rel="noreferrer"><HpIcon.External />HPで見る</a>
      </div>
      {!doc.exists && <p className="hp-note"><HpIcon.Alert />まだ登録されていません。HP には同梱の初期版が表示されています。</p>}
      <div className="hp-row">
        <HpField label="タイトル" required>
          <input className="hp-input" value={doc.title} disabled={!canWrite} onChange={(e) => set({ title: e.target.value })} />
        </HpField>
        <HpField label="最終更新日（表示用）" required hint="例：2026年8月31日。内容を改定したら更新してください">
          <input className="hp-input" value={doc.updatedLabel} disabled={!canWrite} onChange={(e) => set({ updatedLabel: e.target.value })} />
        </HpField>
      </div>
      <details className="hp-help">
        <summary>本文で使える書き方</summary>
        <ul>
          <li><code>**強調**</code> … 太字</li>
          <li><code>[表示文字](/privacypolicy/)</code> … リンク（URL・メールアドレスは自動でリンクになります）</li>
          <li>行頭に <code>- </code> … 箇条書き</li>
          <li><code>| 見出し1 | 見出し2 |</code> の行を続けて書く … 表（1行目が見出し）</li>
          <li>行末に <code>\</code> … 段落内で改行</li>
        </ul>
      </details>
      <ol className="hp-articles">
        {doc.articles.map((a, i) => {
          const isOpen = open.has(i);
          return (
            <li key={i} className={`hp-article${isOpen ? ' is-open' : ''}`}>
              <div className="hp-article__head">
                <button type="button" className="hp-article__toggle" onClick={() => toggle(i)} aria-expanded={isOpen}>
                  {isOpen ? <HpIcon.Up size={14} /> : <HpIcon.Down size={14} />}
                  <span>{a.heading || '（見出しなし）'}</span>
                  {a.subArticles.length > 0 && <HpBadge>{a.subArticles.length} 項</HpBadge>}
                </button>
                {canWrite && (
                  <div className="hp-block__tools">
                    <button type="button" className="hp-icon-btn" onClick={() => moveArticle(i, -1)} disabled={i === 0} aria-label="上へ"><HpIcon.Up /></button>
                    <button type="button" className="hp-icon-btn" onClick={() => moveArticle(i, 1)} disabled={i === doc.articles.length - 1} aria-label="下へ"><HpIcon.Down /></button>
                    <button type="button" className="hp-icon-btn hp-icon-btn--danger" onClick={() => removeArticle(i)} aria-label="条項を削除"><HpIcon.Trash /></button>
                  </div>
                )}
              </div>
              {isOpen && (
                <div className="hp-article__body">
                  <HpField label="見出し" required>
                    <input className="hp-input" value={a.heading} disabled={!canWrite} onChange={(e) => setArticle(i, { heading: e.target.value })} />
                  </HpField>
                  <HpField label="本文">
                    <textarea className="hp-textarea hp-textarea--mono" rows={8} value={a.body} disabled={!canWrite} onChange={(e) => setArticle(i, { body: e.target.value })} />
                  </HpField>
                  {a.subArticles.map((s, k) => (
                    <div key={k} className="hp-subarticle">
                      <div className="hp-subarticle__head">
                        <input className="hp-input" value={s.heading} disabled={!canWrite} onChange={(e) => setSub(i, k, { heading: e.target.value })} aria-label="小見出し" placeholder="小見出し" />
                        {canWrite && (
                          <button type="button" className="hp-icon-btn hp-icon-btn--danger" onClick={() => setArticle(i, { subArticles: a.subArticles.filter((_, j) => j !== k) })} aria-label="小項目を削除"><HpIcon.Trash /></button>
                        )}
                      </div>
                      <textarea className="hp-textarea hp-textarea--mono" rows={5} value={s.body} disabled={!canWrite} onChange={(e) => setSub(i, k, { body: e.target.value })} aria-label="小項目の本文" />
                    </div>
                  ))}
                  {canWrite && (
                    <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => setArticle(i, { subArticles: [...a.subArticles, { heading: '', body: '' }] })}><HpIcon.Plus size={12} />小項目を追加</button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {canWrite && (
        <>
          <button
            type="button"
            className="hp-btn hp-btn--ghost hp-btn--sm"
            onClick={() => { set({ articles: [...doc.articles, { heading: `${doc.articles.length + 1}. `, body: '', subArticles: [] }] }); setOpen((s) => new Set(s).add(doc.articles.length)); }}
          >
            <HpIcon.Plus size={12} />条項を追加
          </button>
          <div className="hp-savebar">
            {dirty && <span className="hp-unsaved">未保存の変更があります</span>}
            {doc.updatedAt && <span className="hp-list__muted">最終保存 {formatDateTime(doc.updatedAt)}</span>}
            <button type="button" className="hp-btn hp-btn--ghost" onClick={load} disabled={!dirty || saving}>変更を取り消す</button>
            <button type="button" className="hp-btn hp-btn--primary" onClick={save} disabled={!dirty || saving}>{saving ? '保存中…' : '保存'}</button>
          </div>
        </>
      )}
    </section>
  );
}

export default function HpLegalPanel({ siteUrl, canWrite, onOpenLegal }) {
  return (
    <div className="hp-panel">
      <PortalDocs siteUrl={siteUrl} onOpenLegal={onOpenLegal} />
      <CrossborderEditor siteUrl={siteUrl} canWrite={canWrite} />
    </div>
  );
}
